import FroalaTextAreaEdit from '@/components/Froala/FroalaTextAreaEdit';
import { useEffect, useContext } from 'react';
import { axios } from '@/axios';
import { config } from '@/config';
import { encode } from '@/base64';
import { NotificationContext } from '@/components/Contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import {
  deep_copy,
  useStateAndRef,
  normalizeNbspHtml,
  normalizeNbspDeep,
} from '@/util';
import { uploadLesson } from '@/query/documents';

const SUBMIT_LESSON_TO_BACKEND = true;

type WidgetState = {
  uid: string;
  inline?: boolean;
  score?: number | string;
  noSubmit?: boolean;
  vraag?: unknown;
  antwoord?: unknown;
  [k: string]: any;
};

type LessonUploadResponse = {
  message: string;
  version: string | null;
  key?: string;
  downloadUrl?: string | null;
  diffText?: string | null;
};

export const LessonEditFroala = ({
  newContent,
  node,
  setSaveFn,
  resetAutoSave,
  isLoading,
  isLesson,
  setIsFroalaLoaded,
  touchUpdatedAt,
  contentLinkTargets,
}: any) => {
  const { setShowNotification } = useContext(NotificationContext);
  const { t } = useTranslation();

  const [isSaving, setIsSaving, isSavingRef] = useStateAndRef(false);
  const [modified, setModified] = useStateAndRef(0);

  const [ref, setRef, refRef] = useStateAndRef(null);
  const [content, setContent, contentRef] = useStateAndRef(null);
  const [savedContent, setSavedContent, savedContentRef] = useStateAndRef(null);
  const [widgets, setWidgets, widgetsRef] = useStateAndRef({});

  const [loaded, setLoaded, loadedRef] = useStateAndRef(false);

  const computeWidgetStats = async (refAny: any) => {
    const editors: Record<string, any> = refAny?.state?.editors || {};
    const editorList = Object.values(editors);

    const settled = await Promise.allSettled(
      editorList.map((x) => x?.stateFn?.())
    );

    const widgetStates: WidgetState[] = settled
      .filter(
        (r): r is PromiseFulfilledResult<unknown> => r.status === 'fulfilled'
      )
      .map((r) => r.value as WidgetState)
      .filter(Boolean);

    let maxScore = 0;
    let onlyTheory = true;

    for (const w of widgetStates) {
      const score =
        typeof w.score === 'string' ? parseFloat(w.score) : w.score ?? 0;
      maxScore += Number.isFinite(score) ? score : 0;
      if (!w.noSubmit) onlyTheory = false;
    }

    return { maxScore, onlyTheory };
  };

  const saveFn = async (autosave = false) => {
    if (loadedRef.current === false) return;
    if (isLoading && !isLesson) return Promise.resolve('ok');
    if (isSavingRef.current) return Promise.resolve('ok');

    resetAutoSave();
    setIsSaving(true);

    try {
      const editorRef: any = refRef.current;
      const clone = (window as any)
        .$(`.lesson_${node?.data?.lessonId} .fr-view`)
        .first()
        .clone();

      if (!clone) return 'ok';

      const promises: Promise<any>[] = [];
      const placeholders = document.querySelectorAll(
        `#${CSS.escape(editorRef?.state?.uid)} .widget-placeholder`
      );

      for (const placeholder of [...placeholders]) {
        try {
          promises.push(editorRef?.state?.editors[placeholder.id]?.stateFn());
        } catch (e) {
          console.error('Unable to parse', placeholder, e);
        }
      }

      const results = await Promise.allSettled(promises);

      let widgetsModfied = false;
      const widgetStates: WidgetState[] = [];

      for (const result of results.reverse()) {
        if (result.status !== 'fulfilled') continue;

        const state: WidgetState | null = result.value
          ? normalizeNbspDeep(result.value)
          : null;
        if (!state) continue;

        widgetStates.push(state);

        const widgetNode = clone.find(`#${CSS.escape(state?.uid)}`);
        const stateStr = encode(JSON.stringify(state));

        const compareState = deep_copy(state);
        delete compareState.vraag;
        delete compareState.antwoord;

        if (
          encode(JSON.stringify(compareState)) !==
          widgetsRef.current[state?.uid]
        ) {
          widgetsModfied = true;
        }

        widgetNode.empty();
        if (state?.inline) {
          widgetNode.append(
            `<span data-open="SOW" data-state="${stateStr}" /><span data-closed="EOW"></span></span>`
          );
        } else {
          widgetNode.append(
            `<div data-open="SOW" data-state="${stateStr}" /><div data-closed="EOW"></div></div>`
          );
        }
      }

      for (const oldWidget of Object.keys(widgetsRef.current)) {
        if (widgetStates.findIndex((x) => x.uid === oldWidget) === -1) {
          widgetsModfied = true;
          break;
        }
      }

      const _content = normalizeNbspHtml(clone.html());

      if (_content === undefined || _content === 'undefined') return 'ok';

      const _orig_content = normalizeNbspHtml(
        savedContentRef.current || contentRef.current
      );

      if (
        !widgetsModfied &&
        (_content === undefined || _orig_content === _content)
      ) {
        return 'ok';
      }

      const postData = node.data.postData;

      let uploadResponse: LessonUploadResponse;

      if (SUBMIT_LESSON_TO_BACKEND) {
        uploadResponse = await uploadLesson(postData, _content);
      } else {
        uploadResponse = {
          message: 'ok',
          version: null,
          downloadUrl: null,
        };
      }

      const { maxScore, onlyTheory } = await computeWidgetStats(editorRef);

      /* 
      //please don't do this, this value is used as counts towards progress
      axios.put(`${config.backend}/use/${node?.data?.usesId}`, {
        onlyTheory,
      });
      */

      await axios.post(`${config.backend}/lessonversions`, {
        lessonId: node?.data?.lessonId,
        lessonType: node.data.lessonType,
        version: uploadResponse.version,
        onlyTheory,
        maxScore,
      });

      // Only mark content saved once both persistence requests succeed, so a
      // failed save can be retried with the same content.
      setSavedContent(_content);

      touchUpdatedAt?.();

      if (!autosave) {
        setShowNotification(t('notifications.lesson_saved'));
      }

      const newWidgets: Record<string, string> = {};
      for (const widget of widgetStates) {
        const cleanWidget = deep_copy(widget);
        delete cleanWidget.vraag;
        delete cleanWidget.antwoord;
        newWidgets[cleanWidget.uid] = encode(JSON.stringify(cleanWidget));
      }
      setWidgets(newWidgets);

      setModified(1);
      return 'ok';
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    setSaveFn(saveFn);
    setModified(0);
  }, [node]);

  useEffect(() => {
    setContent(newContent);
    setIsFroalaLoaded(node?.data?.usesId);
    setLoaded(false);
  }, [newContent]);

  if (!content || content != newContent) return null;

  return (
    <FroalaTextAreaEdit
      toolbarSticky
      key={node?.data?.lessonId}
      lessonId={node?.data?.lessonId}
      contentLinkTargets={contentLinkTargets}
      modus="lessonEdit"
      className={`form-control lesson_${
        node?.data?.lessonId
      } mx-auto w-full max-w-[960px] overflow-hidden border-0 ${
        node?.data?.lessonType === 'instruction' && 'instruction_les'
      }`}
      mode="edit"
      style={{ minHeight: '80vh' }}
      value={content}
      setRef={setRef}
      onLoaded={() => setLoaded(true)}
      onEditors={(editors: any) => {
        const w: Record<string, string> = {};
        for (const editor of Object.values(editors || {})) {
          // const state = (editor as any)?.state;
          const state = normalizeNbspDeep(deep_copy((editor as any)?.state));
          if (!state?.uid) continue;
          delete state?.vraag;
          delete state?.antwoord;
          w[state?.uid] = encode(JSON.stringify(state));
        }
        setWidgets(w);
      }}
      onModified={() => {
        setModified(modified + 1);
      }}
      onSave={async () => {
        saveFn();
      }}
    ></FroalaTextAreaEdit>
  );
};
